import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, unref as _unref } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = { class: "flex flex-wrap items-start justify-between gap-2" }
const _hoisted_3 = { class: "flex items-baseline justify-between gap-2" }
const _hoisted_4 = { class: "flex items-center gap-1.5 text-sm font-medium text-textPrimary" }
const _hoisted_5 = ["aria-label", "checked"]
const _hoisted_6 = { class: "font-mono text-xs text-neutral50" }
const _hoisted_7 = { class: "m-0 text-xs text-neutral50" }
const _hoisted_8 = ["data-testid"]
const _hoisted_9 = { class: "flex flex-wrap items-center gap-1" }
const _hoisted_10 = ["data-testid"]
const _hoisted_11 = ["data-testid"]
const _hoisted_12 = ["data-testid"]
const _hoisted_13 = ["data-testid"]
const _hoisted_14 = ["data-testid"]
const _hoisted_15 = { class: "text-textPrimary" }
const _hoisted_16 = ["data-testid"]
const _hoisted_17 = ["data-testid"]
const _hoisted_18 = ["data-testid"]
const _hoisted_19 = ["data-testid"]
const _hoisted_20 = ["data-testid"]
const _hoisted_21 = { class: "flex flex-wrap gap-1" }
const _hoisted_22 = ["data-testid", "onClick"]

import { computed } from 'vue'

import { QUEUE_TONE_PILL_CLASS } from '@/components/designSystem/QueueViews/types'
import { formatHourMinuteInOffset } from '@/utils/date'
import type {
  PriorityConfig,
  PriorityPaymentRow
} from '@/Network/Types/Responses/PriorityPaymentsResponses'

import { priorityRowActions } from '../actions'
import type { PriorityActionDescriptor } from '../actions'
import {
  PRIORITY_SITUATION_TONES,
  PRIORITY_TYPE_LABELS,
  prioritySituationLabel
} from '../constants'
import { formatPriorityStationTime } from '../rowFormat'
import {
  formatAntecipeStart,
  formatSlaAlso,
  formatSlaKindLabel,
  priorityCriticalReasons,
  priorityThresholdsFor
} from '../slaFormat'
import { formatActivityRecordCell, formatAuditCell } from '../tableColumns'
import PrioritySlaChip from './PrioritySlaChip.vue'
import PrioritySlaCountdown from './PrioritySlaCountdown.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'PriorityPaymentCard',
  props: {
    row: {},
    selectable: { type: Boolean, default: false },
    isSelected: { type: Boolean, default: false },
    nowMs: { default: null },
    config: { default: null },
    testId: { default: 'priority-payment-card' }
  },
  emits: ["toggle-select", "action"],
  setup(__props: any, { emit }) {

const props = __props;





const NOT_STARTED_RECORD_LINE: Partial<Record<PriorityPaymentRow['situation'], string>> = {
  IN_PROGRESS: 'Plantão em andamento; o registro só é cobrado depois do fim.',
  FUTURE: 'Plantão ainda não começou.'
}

const sla = computed(() => props.row.sla ?? null)
const thresholds = computed(() =>
  sla.value ? priorityThresholdsFor(props.config, sla.value.kind) : null
)
const stationTime = computed(() => formatPriorityStationTime(props.row))
const actions = computed(() => priorityRowActions(props.row))
const criticalReasons = computed(() => priorityCriticalReasons(props.row))

/** Sem rótulo de tipo no "Não iniciado" (M3): o prazo embaixo do "—" já explica. */
const kindLabel = computed(() =>
  sla.value && sla.value.state !== 'NOT_STARTED'
    ? formatSlaKindLabel(sla.value, props.row, props.nowMs)
    : null
)

const also = computed(() =>
  sla.value?.secondary
    ? formatSlaAlso(sla.value.secondary, props.row, props.nowMs, props.config)
    : null
)

const antecipeStart = computed(() =>
  sla.value && sla.value.state !== 'NOT_STARTED' ? formatAntecipeStart(props.row) : null
)

const recordLine = computed(() => {
  const upcoming = NOT_STARTED_RECORD_LINE[props.row.situation]
  if (upcoming) return upcoming
  const record = `Registro: ${formatActivityRecordCell(props.row)}`
  if (props.nowMs === null) return record
  return `${record} · Auditoria: ${formatAuditCell(props.row, new Date(props.nowMs).toISOString())}`
})

const claimedAtLabel = computed(() =>
  props.row.followup.claimedAt
    ? (formatHourMinuteInOffset(props.row.followup.claimedAt, props.row.timezone) ?? '')
    : ''
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("article", {
    class: _normalizeClass(["flex flex-col gap-1 rounded-md border bg-white px-2.5 py-2", 
      _ctx.isSelected
        ? 'border-primary shadow-[inset_0_0_0_1px_theme(colors.primary)]'
        : 'border-lightGray30'
    ]),
    "data-testid": _ctx.testId
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(PrioritySlaCountdown, {
        sla: sla.value,
        row: _ctx.row,
        "now-ms": _ctx.nowMs,
        thresholds: thresholds.value,
        "test-id": `${_ctx.testId}-countdown`
      }, null, 8, ["sla", "row", "now-ms", "thresholds", "test-id"]),
      _createVNode(PrioritySlaChip, {
        sla: sla.value,
        "now-ms": _ctx.nowMs,
        thresholds: thresholds.value,
        "test-id": `${_ctx.testId}-chip`
      }, null, 8, ["sla", "now-ms", "thresholds", "test-id"])
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("span", _hoisted_4, [
        (_ctx.selectable)
          ? (_openBlock(), _createElementBlock("input", {
              key: 0,
              type: "checkbox",
              class: "h-4 w-4 cursor-pointer accent-primary",
              "aria-label": `Selecionar plantão ${_ctx.row.doctorStationId}`,
              checked: _ctx.isSelected,
              onChange: _cache[0] || (_cache[0] = ($event: any) => (emit('toggle-select', _ctx.row.doctorStationId)))
            }, null, 40, _hoisted_5))
          : _createCommentVNode("", true),
        _createTextVNode(" " + _toDisplayString(_ctx.row.doctorName), 1)
      ]),
      _createElementVNode("span", _hoisted_6, _toDisplayString(_ctx.row.doctorStationId), 1)
    ]),
    _createElementVNode("p", _hoisted_7, [
      _createTextVNode(_toDisplayString(_ctx.row.healthUnitName ?? _ctx.row.scheduleName) + " · " + _toDisplayString(stationTime.value.text) + " ", 1),
      _createElementVNode("span", {
        class: _normalizeClass(["ml-1 rounded px-1.5 py-0.5 font-mono text-[11px]", 
          stationTime.value.isOutsideBrasilia
            ? 'bg-blue-50 text-blue-700'
            : 'bg-lightGray30 text-neutral50'
        ]),
        "data-testid": `${_ctx.testId}-timezone`
      }, _toDisplayString(stationTime.value.timezoneLabel), 11, _hoisted_8)
    ]),
    _createElementVNode("div", _hoisted_9, [
      (kindLabel.value)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: _normalizeClass(["rounded border px-1.5 py-px text-[11px] font-bold", 
          sla.value?.kind === 'ANTECIPE' ? 'border-blue-300 text-blue-700' : 'border-primary text-primary'
        ]),
            "data-testid": `${_ctx.testId}-sla-kind`
          }, _toDisplayString(kindLabel.value), 11, _hoisted_10))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.row.types, (type, index) => {
        return (_openBlock(), _createElementBlock("span", {
          key: `${type}-${index}`,
          class: _normalizeClass(["rounded border border-current px-1.5 py-px text-[11px] font-bold", 
          type.startsWith('IN_CASH')
            ? 'text-primary'
            : type === 'ANTECIPE_PAID'
              ? 'text-red-700'
              : 'text-blue-700'
        ]),
          "data-testid": `${_ctx.testId}-type-${index}`
        }, _toDisplayString(_unref(PRIORITY_TYPE_LABELS)[type]), 11, _hoisted_11))
      }), 128)),
      _createElementVNode("span", {
        class: _normalizeClass(["rounded-full px-2 py-0.5 text-xs font-medium", _unref(QUEUE_TONE_PILL_CLASS)[_unref(PRIORITY_SITUATION_TONES)[_ctx.row.situation]]]),
        "data-testid": `${_ctx.testId}-situation`
      }, _toDisplayString(_unref(prioritySituationLabel)(_ctx.row)), 11, _hoisted_12),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(criticalReasons.value, (reason) => {
        return (_openBlock(), _createElementBlock("span", {
          key: reason,
          class: "rounded-full border border-red-200 bg-white px-2 py-0.5 text-[11px] font-medium text-red-700",
          "data-testid": `${_ctx.testId}-critical`
        }, _toDisplayString(reason), 9, _hoisted_13))
      }), 128))
    ]),
    (also.value)
      ? (_openBlock(), _createElementBlock("p", {
          key: 0,
          class: "m-0 text-xs text-neutral50",
          "data-testid": `${_ctx.testId}-also`
        }, [
          _createTextVNode(" Também: "),
          _createElementVNode("b", _hoisted_15, _toDisplayString(also.value.kindLabel), 1),
          _createTextVNode(" · " + _toDisplayString(also.value.detail), 1)
        ], 8, _hoisted_14))
      : _createCommentVNode("", true),
    (_ctx.row.activityRecord?.rejectionJustification)
      ? (_openBlock(), _createElementBlock("p", {
          key: 1,
          class: "m-0 max-w-[38ch] border-l-2 border-red-600 pl-2 text-xs text-neutral50",
          "data-testid": `${_ctx.testId}-justification`
        }, _toDisplayString(_ctx.row.activityRecord.rejectionJustification), 9, _hoisted_16))
      : _createCommentVNode("", true),
    _createElementVNode("p", {
      class: "m-0 text-[11px] text-neutral50",
      "data-testid": `${_ctx.testId}-record`
    }, _toDisplayString(recordLine.value), 9, _hoisted_17),
    (antecipeStart.value)
      ? (_openBlock(), _createElementBlock("p", {
          key: 2,
          class: "m-0 text-[11px] text-neutral50",
          "data-testid": `${_ctx.testId}-antecipe-start`
        }, " Início do SLA Antecipe: " + _toDisplayString(antecipeStart.value), 9, _hoisted_18))
      : _createCommentVNode("", true),
    (_ctx.row.followup.claimedByName)
      ? (_openBlock(), _createElementBlock("p", {
          key: 3,
          class: "m-0 text-[11px] text-neutral50",
          "data-testid": `${_ctx.testId}-claim`
        }, " em tratativa por " + _toDisplayString(_ctx.row.followup.claimedByName) + " desde " + _toDisplayString(claimedAtLabel.value), 9, _hoisted_19))
      : _createCommentVNode("", true),
    (_ctx.row.doctorPhone)
      ? (_openBlock(), _createElementBlock("p", {
          key: 4,
          class: "m-0 text-[11px] text-neutral50",
          "data-testid": `${_ctx.testId}-phone`
        }, _toDisplayString(_ctx.row.doctorPhone), 9, _hoisted_20))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_21, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(actions.value, (action) => {
        return (_openBlock(), _createElementBlock("button", {
          key: action.key,
          type: "button",
          class: "rounded-[5px] border border-lightGray30 bg-transparent px-2 py-0.5 text-xs text-primary",
          "data-testid": action.testId,
          onClick: ($event: any) => (emit('action', { key: action.key, row: _ctx.row }))
        }, _toDisplayString(action.label), 9, _hoisted_22))
      }), 128))
    ])
  ], 10, _hoisted_1))
}
}

})